import detailMessage from './detailMessage';
import React, { useEffect, useState, useCallback, useRef, ChangeEvent, FormEvent } from "react";
import {
  fetchWorkingHours,
  updateWorkingHours,
  removeWorkingHours,
  getVacations,
  createVacation,
  updateVacation,
  deleteVacation,
  getRecurringClosures,
  createRecurringClosure,
  deleteRecurringClosure,
} from "./api";
import { CheckIcon, PenIcon, UmbrellaIcon, PlusIcon, TrashIcon, ResetIcon } from "./components/Icons";
import { Vacation, RecurringClosure } from "./types";
import { useLang } from "./LangContext";
import "./AvailabilityManager.css";

interface HourEntry {
  start: number | "";
  end: number | "";
}

interface Toast {
  message: string;
  type: "error" | "success";
  visible: boolean;
}

interface VacationFormData {
  title: string;
  start_date: string;
  end_date: string;
}

interface Props {
  token: string;
}

export default function AvailabilityManager({ token }: Props) {
  const { lang, t } = useLang();

  const weekdays = [
    t.weekdays[0].full,
    t.weekdays[1].full,
    t.weekdays[2].full,
    t.weekdays[3].full,
    t.weekdays[4].full,
    t.weekdays[5].full,
    t.weekdays[6].full,
  ];

  const [activeTab, setActiveTab] = useState<"availability" | "vacation">("availability");
  const [hours, setHours] = useState<Record<string, HourEntry>>({});
  const [toast, setToast] = useState<Toast>({ message: "", type: "error", visible: false });
  const [savingDay, setSavingDay] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string, type: "error" | "success" = "error") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type, visible: true });
    toastTimer.current = setTimeout(() => setToast({ message: "", type: "error", visible: false }), 4000);
  }, []);

  const [vacations, setVacations] = useState<Vacation[]>([]);
  const [vacationLoading, setVacationLoading] = useState(true);
  const [vacationError, setVacationError] = useState("");
  const [showVacationForm, setShowVacationForm] = useState(false);
  const [editingVacation, setEditingVacation] = useState<Vacation | null>(null);
  const [vacationFormData, setVacationFormData] = useState<VacationFormData>({
    title: "",
    start_date: "",
    end_date: "",
  });

  const [closures, setClosures] = useState<RecurringClosure[]>([]);
  const [closureError, setClosureError] = useState("");
  const [closureForm, setClosureForm] = useState({ weekday: "0", interval_weeks: "2", reference_date: "" });

  const fetchClosures = useCallback(async () => {
    try {
      const data = await getRecurringClosures(token);
      setClosures(data);
    } catch {
      // non-critical
    }
  }, [token]);

  const handleClosureSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setClosureError("");
    const weekday = parseInt(closureForm.weekday, 10);
    const interval = parseInt(closureForm.interval_weeks, 10);
    // JS getDay(): 0=Sun,1=Mon..6=Sat → Python weekday: 0=Mon..6=Sun
    const jsDay = new Date(closureForm.reference_date + "T00:00:00").getDay();
    const pyDay = jsDay === 0 ? 6 : jsDay - 1;
    if (pyDay !== weekday) {
      setClosureError(`${t.recurringClosureWrongWeekday} ${t.weekdays[weekday].full}`);
      return;
    }
    try {
      await createRecurringClosure(weekday, interval, closureForm.reference_date, token);
      await fetchClosures();
      setClosureForm({ weekday: "0", interval_weeks: "2", reference_date: "" });
    } catch {
      setClosureError(t.recurringClosureError);
    }
  };

  const nextClosureDates = (c: RecurringClosure, count: number): string[] => {
    const ref = new Date(c.reference_date + "T00:00:00");
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    // Convert Python weekday (0=Mon) to JS getDay() (0=Sun)
    const targetJsDay = c.weekday === 6 ? 0 : c.weekday + 1;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Advance to the nearest upcoming target weekday
    const candidate = new Date(today);
    const daysAhead = (targetJsDay - candidate.getDay() + 7) % 7;
    candidate.setDate(candidate.getDate() + (daysAhead === 0 ? 0 : daysAhead));
    // Find first candidate on the pattern (within one interval window)
    for (let i = 0; i < c.interval_weeks; i++) {
      const diffWeeks = Math.round((candidate.getTime() - ref.getTime()) / msPerWeek);
      if (diffWeeks % c.interval_weeks === 0) break;
      candidate.setDate(candidate.getDate() + 7);
    }
    const results: string[] = [];
    for (let i = 0; i < count; i++) {
      const [year, month, day] = [candidate.getFullYear(), candidate.getMonth(), candidate.getDate()];
      results.push(new Date(year, month, day).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US'));
      candidate.setDate(candidate.getDate() + c.interval_weeks * 7);
    }
    return results;
  };

  const handleClosureDelete = async (id: number) => {
    setClosureError("");
    try {
      await deleteRecurringClosure(id, token);
      await fetchClosures();
    } catch {
      setClosureError(t.recurringClosureError);
    }
  };

  const fetchVacations = useCallback(async () => {
    try {
      setVacationLoading(true);
      const data = await getVacations(token);
      setVacations(data);
      setVacationError("");
    } catch (err) {
      setVacationError(t.failedLoadVacations);
      console.error("Error fetching vacations:", err);
    } finally {
      setVacationLoading(false);
    }
  }, [token, t.failedLoadVacations]);

  useEffect(() => {
    fetchWorkingHours(token)
      .then((data) => {
        const mapped: Record<string, HourEntry> = {};
        for (const entry of data) {
          mapped[entry.day] = { start: entry.start_hour, end: entry.end_hour };
        }
        setHours(mapped);
      })
      .catch(() => showToast(t.failedLoadWorkingHours));

    fetchVacations();
    fetchClosures();
  }, [token, fetchVacations, fetchClosures, showToast, t.failedLoadWorkingHours]);

  const validateHours = (start: number | "", end: number | ""): string | null => {
    if (start === "" || start == null || end === "" || end == null) {
      return t.validateBothHours;
    }
    const s = Number(start);
    const e = Number(end);
    if (s < 0 || s > 23) return t.validateStartHour;
    if (e < 0 || e > 23) return t.validateEndHour;
    if (e <= s) return t.validateEndAfterStart;
    return null;
  };

  const handleChange = (day: string, field: "start" | "end", value: string) => {
    setHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value === "" ? "" : Number(value),
      },
    }));
  };

  const handleSave = async (day: string) => {
    const { start, end } = hours[day] || {};
    const error = validateHours(start, end);
    if (error) {
      showToast(error);
      return;
    }
    setSavingDay(day);
    try {
      await updateWorkingHours(token, day, Number(start), Number(end));
      showToast(t.workingHoursSaved, "success");
    } catch {
      showToast(t.failedSaveWorkingHours);
    } finally {
      setSavingDay(null);
    }
  };

  const handleRemove = async (day: string) => {
    try {
      await removeWorkingHours(token, day);
      const updatedHours = { ...hours };
      delete updatedHours[day];
      setHours(updatedHours);
      showToast(t.workingHoursReset(day), "success");
    } catch {
      showToast(t.failedResetWorkingHours);
    }
  };

  const handleVacationSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setVacationError("");
    try {
      if (editingVacation) {
        await updateVacation(editingVacation.id, vacationFormData, token);
      } else {
        await createVacation(vacationFormData, token);
      }
      await fetchVacations();
      handleVacationCancel();
    } catch (err) {
      setVacationError(detailMessage(err, t.failedSaveVacation));
    }
  };

  const handleVacationEdit = (vacation: Vacation) => {
    setEditingVacation(vacation);
    setVacationFormData({
      title: vacation.title,
      start_date: vacation.start_date,
      end_date: vacation.end_date,
    });
    setShowVacationForm(true);
  };

  const handleVacationDelete = async (vacationId: number) => {
    if (window.confirm(t.deleteVacationConfirm)) {
      try {
        await deleteVacation(vacationId, token);
        await fetchVacations();
      } catch {
        setVacationError(t.failedDeleteVacation);
      }
    }
  };

  const handleVacationCancel = () => {
    setShowVacationForm(false);
    setEditingVacation(null);
    setVacationFormData({ title: "", start_date: "", end_date: "" });
    setVacationError("");
  };

  const handleVacationInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setVacationFormData({ ...vacationFormData, [e.target.name]: e.target.value });
  };

  const formatVacationRange = (startDate: string, endDate: string): string => {
    const fmt = (d: string) => {
      const [year, month, day] = d.split("-").map(Number);
      return new Date(year, month - 1, day).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US');
    };
    return `${fmt(startDate)} – ${fmt(endDate)}`;
  };

  return (
    <div className="availability-container">
      <div className="availability-header">
        <h3 className="availability-title">{t.timeManagementHeading}</h3>
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === "availability" ? "active" : ""}`}
            onClick={() => setActiveTab("availability")}
          >
            {t.tabWorkingHours}
          </button>
          <button
            className={`tab-button ${activeTab === "vacation" ? "active" : ""}`}
            onClick={() => setActiveTab("vacation")}
          >
            {t.tabVacations}
          </button>
        </div>
      </div>

      <div className="tab-panels">
        <div className={`tab-content${activeTab !== "availability" ? " tab-hidden" : ""}`}>
          <div className="availability-section">
            <h4 className="section-title">{t.manageWeeklyAvailability}</h4>
            {weekdays.map((day) => (
              <div className="availability-row" key={day}>
                <div className="day-circle" aria-hidden="true">{day[0]}</div>
                <div className="day-label">{day}</div>
                <div className="time-inputs">
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={hours[day]?.start ?? ""}
                    onChange={(e) => handleChange(day, "start", e.target.value)}
                    placeholder=""
                    className="hour-input"
                  />
                  <span className="to-label">{t.toLabel}</span>
                  <input
                    type="number"
                    min="1"
                    max="23"
                    value={hours[day]?.end ?? ""}
                    onChange={(e) => handleChange(day, "end", e.target.value)}
                    placeholder=""
                    className="hour-input"
                  />
                </div>
                <div className="button-group">
                  <button
                    className="button button-primary"
                    onClick={() => handleSave(day)}
                    disabled={savingDay === day}
                    title={t.saveButton}
                    aria-label={`${t.saveButton} ${day}`}
                  >
                    <CheckIcon /><span className="button-label">{t.saveButton}</span>
                  </button>
                  <button
                    className="button button-edit subtle"
                    onClick={() => handleRemove(day)}
                    disabled={!hours[day]}
                    title="Reset"
                    aria-label={`Reset ${day}`}
                  >
                    <ResetIcon /><span className="button-label">Reset</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={`tab-content tab-vacation${activeTab !== "vacation" ? " tab-hidden" : ""}`}>
          <div className="recurring-closures-section">
            <h4 className="section-title">{t.recurringClosuresHeading}</h4>
            <p className="recurring-closures-sub">{t.recurringClosuresSubheading}</p>
            {closureError && <div className="error-message">{closureError}</div>}
            <form className="closure-form" onSubmit={handleClosureSubmit}>
              <div className="closure-form-row">
                <label className="closure-label">{t.recurringClosureWeekdayLabel}</label>
                <select
                  value={closureForm.weekday}
                  onChange={(e) => setClosureForm({ ...closureForm, weekday: e.target.value })}
                  className="closure-select"
                >
                  {t.weekdays.map((day, idx) => (
                    <option key={idx} value={idx}>{day.full}</option>
                  ))}
                </select>
                <label className="closure-label">{t.recurringClosureIntervalLabel}</label>
                <input
                  type="number"
                  min="1"
                  max="52"
                  value={closureForm.interval_weeks}
                  onChange={(e) => setClosureForm({ ...closureForm, interval_weeks: e.target.value })}
                  className="closure-interval-input"
                  required
                />
                <span className="closure-unit">{t.recurringClosureIntervalUnit}</span>
              </div>
              <div className="closure-form-row">
                <label className="closure-label">{t.recurringClosureReferenceDateLabel}</label>
                <input
                  type="date"
                  lang={lang === 'de' ? 'de' : 'en'}
                  value={closureForm.reference_date}
                  onChange={(e) => setClosureForm({ ...closureForm, reference_date: e.target.value })}
                  className="closure-date-input"
                  required
                />
                <button type="submit" className="button button-primary closure-add-button">
                  {t.recurringClosureAddButton}
                </button>
              </div>
            </form>
            {closures.length > 0 && (
              <ul className="closure-list">
                {closures.map((c) => (
                  <li key={c.id} className="closure-item">
                    <div className="closure-item-text">
                      <span className="closure-item-label">{t.recurringClosureDisplay(c.interval_weeks, c.weekday_name)}</span>
                      <span className="closure-item-next">{t.recurringClosureNext} {nextClosureDates(c, 2).join(" · ")}</span>
                    </div>
                    <button
                      className="button button-delete button-sm"
                      onClick={() => handleClosureDelete(c.id)}
                    >
                      <TrashIcon />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="vacation-section-divider" />

          <div className="vacation-section">
            <h4 className="section-title">{t.vacationManagementHeading}</h4>

            {!showVacationForm && (
              <button
                type="button"
                className="vacation-add-row"
                onClick={() => setShowVacationForm(true)}
              >
                <span className="vacation-circle vacation-circle-add" aria-hidden="true">
                  <PlusIcon />
                </span>
                <span className="vacation-add-label">{t.addNewVacation}</span>
              </button>
            )}

            {vacationError && <div className="error-message">{vacationError}</div>}

            {showVacationForm && (
              <form className="vacation-form" onSubmit={handleVacationSubmit}>
                <h5 className="form-title">{editingVacation ? t.vacationFormTitleEdit : t.vacationFormTitleAdd}</h5>

                <div className="form-group">
                  <label htmlFor="title">{t.vacationTitleLabel}</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={vacationFormData.title}
                    onChange={handleVacationInputChange}
                    required
                    placeholder="e.g., Summer Vacation"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="start_date">{t.vacationStartDateLabel}</label>
                    <input
                      type="date"
                      id="start_date"
                      name="start_date"
                      lang={lang === 'de' ? 'de' : 'en'}
                      value={vacationFormData.start_date}
                      onChange={handleVacationInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="end_date">{t.vacationEndDateLabel}</label>
                    <input
                      type="date"
                      id="end_date"
                      name="end_date"
                      lang={lang === 'de' ? 'de' : 'en'}
                      value={vacationFormData.end_date}
                      onChange={handleVacationInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="button button-success">
                    {editingVacation ? t.updateVacationButton : t.createVacationButton}
                  </button>
                  <button type="button" className="button button-secondary" onClick={handleVacationCancel}>
                    {t.cancelButton}
                  </button>
                </div>
              </form>
            )}

            <div className="vacation-list">
              {vacationLoading ? (
                <div className="vacation-loading">{t.loadingVacations}</div>
              ) : vacations.length === 0 ? (
                <p className="no-vacations">{t.noVacationsScheduled}</p>
              ) : (
                vacations.map((vacation) => (
                  <div key={vacation.id} className="vacation-item">
                    <span className="vacation-circle" aria-hidden="true">
                      <UmbrellaIcon />
                    </span>
                    <div className="vacation-content">
                      <div className="vacation-title">{vacation.title}</div>
                      <div className="vacation-dates">
                        {formatVacationRange(vacation.start_date, vacation.end_date)}
                      </div>
                    </div>
                    <div className="vacation-actions">
                      <button
                        className="button button-edit"
                        onClick={() => handleVacationEdit(vacation)}
                      >
                        <PenIcon /> {t.editButton}
                      </button>
                      <button
                        className="button button-delete"
                        onClick={() => handleVacationDelete(vacation.id)}
                      >
                        <TrashIcon /> {t.deleteButton}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      {toast.visible && (
        <div className={`toast toast-${toast.type}`} role="alert">
          <span>{toast.message}</span>
          <button
            className="toast-close"
            onClick={() => setToast({ message: "", type: "error", visible: false })}
            aria-label={t.dismissToast}
          >✕</button>
        </div>
      )}
    </div>
  );
}
